import { useState, useEffect, useCallback } from 'react';
import { useHttpClient, useAuth, UserPreferencesService } from '@actschurch/ccms-sdk';
import { usePreferencesStore } from '../../../stores';

interface SyncState {
  status: 'idle' | 'loading' | 'syncing' | 'success' | 'error';
  lastSynced: Date | null;
  error: string | null;
}

export function usePreferencesSync() {
  const [syncState, setSyncState] = useState<SyncState>({
    status: 'idle',
    lastSynced: null,
    error: null,
  });

  const httpClient = useHttpClient();
  const { state: authState } = useAuth();
  const isAuthenticated = authState.isAuthenticated;

  // Load from server on mount and apply if newer
  useEffect(() => {
    if (!isAuthenticated) return;

    const loadFromServer = async () => {
      setSyncState((prev) => ({ ...prev, status: 'loading' }));

      try {
        const userPreferencesService = new UserPreferencesService(httpClient);
        const serverPrefs = await userPreferencesService.getMyPreferencesByApp('1');

        const lastSynced = serverPrefs.modifiedDateTime
          ? new Date(serverPrefs.modifiedDateTime * 1000)
          : null;

        // Parse and apply server preferences if they exist and are newer
        if (serverPrefs.preferences) {
          try {
            const parsed = JSON.parse(serverPrefs.preferences);
            const localModifiedTime = usePreferencesStore.getState().lastModifiedTime;
            const serverModifiedTime = serverPrefs.modifiedDateTime;

            if (!localModifiedTime || (serverModifiedTime && serverModifiedTime > localModifiedTime)) {
              usePreferencesStore.getState().updatePreferences({
                ...parsed,
                lastSyncTime: serverModifiedTime,
              });
            }
          } catch {
            // Failed to parse server preferences, keep local
          }
        }

        setSyncState({
          status: 'idle',
          lastSynced,
          error: null,
        });
      } catch (error) {
        // No server data or error - use local
        setSyncState({ status: 'idle', lastSynced: null, error: null });
      }
    };

    loadFromServer();
  }, [isAuthenticated, httpClient]);

  // Sync to cloud
  const syncToCloud = useCallback(async () => {
    if (!isAuthenticated) {
      setSyncState((prev) => ({ ...prev, status: 'error', error: 'User not logged in' }));
      return;
    }

    setSyncState((prev) => ({ ...prev, status: 'syncing', error: null }));

    try {
      // Get all preferences from Zustand
      const state = usePreferencesStore.getState();
      const prefsData = {
        colorSchemeMode: state.colorSchemeMode,
        colorSchemeKey: state.colorSchemeKey,
        accentColor: state.accentColor,
        language: state.language,
        locale: state.locale,
        timezone: state.timezone,
        dateFormat: state.dateFormat,
        timeFormat: state.timeFormat,
        layoutWidth: state.layoutWidth,
        layoutPadding: state.layoutPadding,
        reducedMotion: state.reducedMotion,
        highContrast: state.highContrast,
        fontSize: state.fontSize,
        strongFocus: state.strongFocus,
        grayscaleMode: state.grayscaleMode,
        lineSpacing: state.lineSpacing,
        letterSpacing: state.letterSpacing,
        showIconLabels: state.showIconLabels,
        enableTooltips: state.enableTooltips,
        textToSpeech: state.textToSpeech,
        uiSize: state.uiSize,
        menuIconOnly: state.menuIconOnly,
        menuAutoHide: state.menuAutoHide,
        menuDensity: state.menuDensity,
        menuShowSectionCounts: state.menuShowSectionCounts,
        daylightSaving: state.daylightSaving,
      };

      const prefsJson = JSON.stringify(prefsData);
      const userPreferencesService = new UserPreferencesService(httpClient);

      // Try to get existing record
      try {
        const existing = await userPreferencesService.getMyPreferencesByApp('1');

        // Update existing
        await userPreferencesService.updateMyPreferences(existing.id, {
          preferences: prefsJson,
        });
      } catch {
        // Create new
        await userPreferencesService.createMyPreferences({
          appId: '1',
          preferences: prefsJson,
        });
      }

      setSyncState({
        status: 'success',
        lastSynced: new Date(),
        error: null,
      });

      // Update lastSyncTime in store for conflict resolution
      usePreferencesStore.getState().updatePreferences({
        lastSyncTime: Math.floor(Date.now() / 1000),
      });

      // Reset to idle after 3 seconds
      setTimeout(() => {
        setSyncState((prev) => ({ ...prev, status: 'idle' }));
      }, 3000);
    } catch (error: any) {
      console.error('Sync failed:', error);
      setSyncState({
        status: 'error',
        lastSynced: null,
        error: error.message || 'Unknown error',
      });
    }
  }, [isAuthenticated, httpClient]);

  return { syncState, syncToCloud };
}
